import { clsx } from 'clsx'
import { useEffect, useState } from 'react'
import { useLocation } from 'react-router'
import { CONTACT_TOPICS, contactLabel, contactPurpose, emptyField, formatPhone, needsReview, type ContactsSection as Doc, type FaqSection, type FieldDef, type LocationsSection, type PortalConfig } from '@sonder/shared'
import { useClientView, useMode } from '@/app/mode'
import { useSectionEditor } from '@/data/editor'
import { Button, StatusPill, useConfirm } from '@/ui'
import { AddRow, BlockHead, NoEntities, useBrands, useLocations } from './common'
import { SHARED_CONTACTS, contactGroup, focusContact, focusContactGroup, newContact, newContactId, type Contact, type ContactsNavState } from './contactHelpers'
import { FieldEditor } from './FieldEditor'

const FIELDS: Record<'name' | 'role' | 'phone' | 'email', FieldDef> = {
  phone: { key: 'phone', label: 'Phone', kind: 'phone', required: false },
  email: { key: 'email', label: 'Email', kind: 'email', required: false },
  name: { key: 'name', label: 'Name', kind: 'text', required: false },
  role: { key: 'role', label: 'Title', kind: 'text', required: false },
}

type Editor = ReturnType<typeof useSectionEditor<Doc>>
const digits = (s: string) => s.replace(/\D/g, '').replace(/^1(?=\d{10}$)/, '')

export function ContactsSection({ clientId }: { clientId: string; config: PortalConfig }) {
  const ed = useSectionEditor<Doc>(clientId, 'contacts')
  const locations = useLocations(clientId)
  const brands = useBrands(clientId)
  const locEd = useSectionEditor<LocationsSection>(clientId, 'locations')
  const faqEd = useSectionEditor<FaqSection>(clientId, 'faq')
  const mode = useMode()
  const confirm = useConfirm()
  const clientView = useClientView()
  const nav = useLocation()
  // Cards start collapsed to a one-line preview; ids here are expanded.
  const [expanded, setExpanded] = useState<ReadonlySet<string>>(() => new Set())
  const expand = (id: string) => setExpanded((s) => (s.has(id) ? s : new Set(s).add(id)))
  const toggleCard = (id: string) =>
    setExpanded((s) => {
      const next = new Set(s)
      if (!next.delete(id)) next.add(id)
      return next
    })
  const { focusContact: focusId, focusGroup } = (nav.state as ContactsNavState | null) ?? {}
  const items = ed.doc?.items
  // Groups depend on which brand each location belongs to, so wait for locations before scrolling to one.
  const ready = Boolean(items) && brands.order.length > 0 && !locations.loading
  // Arriving from another step: jump to the contact it named, or to a brand's group to add one there.
  useEffect(() => {
    if (!focusId || !items?.[focusId]) return
    expand(focusId)
    focusContact(focusId)
  }, [focusId, items])
  useEffect(() => {
    if (focusGroup && ready) focusContactGroup(focusGroup)
  }, [focusGroup, ready])
  if (!ed.doc) return null
  if (brands.order.length === 0) return <NoEntities what="brands" hint="Contacts are listed per brand; add brands first." />

  const contacts = Object.entries(items ?? {}).sort(([, a], [, b]) => a.createdAt - b.createdAt)
  const add = (init: Parameters<typeof newContact>[0] = {}) => {
    const id = newContactId()
    ed.setRaw(`items.${id}`, newContact(init, mode))
    expand(id)
    focusContact(id)
  }
  const remove = async (id: string, c: Contact) => {
    if (!(await confirm({ title: `Remove ${contactLabel(c)}?`, body: 'Locations that used it will show no main line, and FAQ questions it was assigned to will need someone else.', confirmLabel: 'Remove' }))) return
    ed.remove(`items.${id}`)
    for (const [bid, qs] of Object.entries(faqEd.doc?.brands ?? {})) {
      for (const [qid, a] of Object.entries(qs)) if (a.contacts?.includes(id)) faqEd.setRaw(`brands.${bid}.${qid}.contacts`, a.contacts.filter((x) => x !== id))
    }
  }
  // Locations had their own Phone box before Contacts existed; those numbers are still saved on the location.
  // Moving one makes it a main-line contact and clears the old copy, so each number lives in one place.
  const moveToMainLine = (lid: string, phone: string) => {
    add({ phone: formatPhone(phone), topics: ['phone'], locationIds: [lid] })
    locEd.setRaw(`items.${lid}.phone`, emptyField(''))
  }
  // Saved location numbers not in the list yet.
  const known = new Set(contacts.map(([, c]) => digits(c.phone?.value ?? '')).filter(Boolean))
  const strayPhones = locations.order
    .map((lid) => ({ lid, phone: locEd.doc?.items[lid]?.phone?.value?.trim() ?? '' }))
    .filter((p) => p.phone && !known.has(digits(p.phone)))

  // One group per brand, then (with several brands) one for contacts that cover every brand.
  const brandOf = (lid: string) => locations.items[lid]?.brandId
  const groupOf = (c: Contact) => contactGroup(c, brands.order, brandOf)
  const several = brands.order.length > 1
  const groups = [
    ...brands.order.map((b) => ({
      id: b,
      title: brands.name(b),
      hint: '',
      addLabel: several ? `Add a contact for ${brands.name(b)}` : 'Add a contact',
      // A new contact starts on this brand's locations, so it is listed here.
      locationIds: several ? locations.order.filter((l) => brandOf(l) === b) : [],
    })),
    ...(several ? [{ id: SHARED_CONTACTS, title: 'Every brand', hint: 'People and inboxes that handle all of your locations, like PR or a group events team.', addLabel: 'Add a shared contact', locationIds: [] as string[] }] : []),
  ]

  return (
    <div className="grid gap-12">
      {strayPhones.length > 0 && (
        <section aria-label="Numbers on your locations" className="grid max-w-form gap-3 border-l-2 border-warn bg-warn-wash px-5 py-4">
          <div className="grid gap-1">
            <p className="text-eyebrow uppercase tracking-eyebrow text-ink">Numbers saved on your locations</p>
            <p className="text-small text-charcoal">Entered on Locations & hours before this step existed. Add each as that location’s main line to keep it; it then lives only here.</p>
          </div>
          <ul className="grid gap-2 text-small">
            {strayPhones.map((p) => (
              <li key={p.lid} className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                <span className="text-ink">{locations.name(p.lid)}</span>
                <span className="text-charcoal tabular-nums">{p.phone}</span>
                <button type="button" onClick={() => moveToMainLine(p.lid, p.phone)} className="ml-auto text-ink underline underline-offset-4">
                  Add as main line
                </button>
              </li>
            ))}
          </ul>
        </section>
      )}
      {contacts.length === 0 && <NoEntities what="contact information" hint="Start with each location’s main line, then private events, reservations and management." />}
      {groups.map((g) => {
        const list = contacts.filter(([, c]) => groupOf(c) === g.id)
        return (
          <section key={g.id} aria-label={g.title} data-contact-group={g.id} className="grid scroll-mt-24 gap-6 border-t border-ink pt-6">
            <BlockHead eyebrow="Contacts" title={g.title}>
              {g.hint && <p className="mt-1 text-small text-dim">{g.hint}</p>}
            </BlockHead>
            {list.length === 0 && <p className="text-small text-dim">No contacts here yet.</p>}
            {list.map(([id, c]) => (
              <ContactCard key={id} id={id} c={c} ed={ed} locations={locations} open={expanded.has(id)} onToggle={() => toggleCard(id)} toConfirm={clientView} onRemove={() => void remove(id, c)} />
            ))}
            <AddRow label={g.addLabel} onAdd={() => add({ locationIds: g.locationIds })} />
          </section>
        )
      })}
    </div>
  )
}

type CardProps = {
  id: string
  c: Contact
  ed: Editor
  locations: ReturnType<typeof useLocations>
  open: boolean
  onToggle: () => void
  /** Client view: flag a collapsed card that still has prefilled values to confirm. */
  toConfirm: boolean
  onRemove: () => void
}

function ContactCard({ id, c, ed, locations, open, onToggle, toConfirm, onRemove }: CardProps) {
  const topics = c.topics ?? []
  const where = c.locationIds ?? []
  const toggle = (list: string[], v: string) => (list.includes(v) ? list.filter((x) => x !== v) : [...list, v])
  const whereText = where.length ? where.map((l) => locations.name(l)).join(', ') : locations.order.length > 1 ? 'All locations' : ''
  const bodyId = `contact-${id}-body`
  // A named contact's preview says what it's for; an unnamed one is already labelled by it.
  const purpose = c.name?.value?.trim() ? contactPurpose(c) : ''
  const reach = [c.phone?.value, c.email?.value].filter(Boolean).join(' · ')
  const unconfirmed = toConfirm && (['phone', 'email', 'name', 'role'] as const).some((k) => needsReview(c[k]))
  return (
    <section aria-label={contactLabel(c)} data-contact={id} className={clsx('grid max-w-form border border-gray-200', open && 'gap-6 pb-5')}>
      <header className="flex items-start gap-3">
        {/* The heading wraps the toggle (accordion pattern); body type so the preview lines read like the rest of the card. */}
        <h3 className="flex min-w-0 flex-1 font-body text-body text-charcoal">
          {/* On a phone "To confirm" goes under the preview, so the name and locations keep the card's full width. */}
          <button type="button" aria-expanded={open} aria-controls={bodyId} onClick={onToggle} className="flex min-w-0 flex-1 items-start gap-3 p-5 pr-0 text-left max-sm:flex-col max-sm:gap-2">
            <span className="grid min-w-0 flex-1 gap-0.5 max-sm:self-stretch">
              <span className="text-h5 font-body font-medium leading-tight text-ink">{contactLabel(c)}</span>
              {whereText && <span className="text-small text-dim">{whereText}</span>}
              {!open && (reach || purpose) && (
                <span className="mt-1 flex flex-wrap gap-x-4 gap-y-0.5 text-small">
                  {reach && <span className="text-charcoal tabular-nums">{reach}</span>}
                  {purpose && <span className="text-dim">{purpose}</span>}
                </span>
              )}
              {!open && !reach && <span className="mt-1 text-small text-dim">No number or email yet</span>}
            </span>
            {!open && unconfirmed && <StatusPill tone="warn">To confirm</StatusPill>}
          </button>
        </h3>
        <div className="flex items-center gap-4 p-5 pl-0">
          {open && (
            <Button variant="danger-link" size="sm" arrow={false} onClick={onRemove}>
              Remove
            </Button>
          )}
          <button type="button" onClick={onToggle} aria-label={open ? 'Collapse' : 'Expand'} aria-controls={bodyId} className="text-dim hover:text-ink">
            <span aria-hidden="true" className={clsx('inline-block transition-transform', open && 'rotate-45')}>+</span>
          </button>
        </div>
      </header>
      {open && (
        <div id={bodyId} className="grid gap-6 px-5">
          <Chips label="What’s it for?" hint="Tag everything this number or inbox handles." options={CONTACT_TOPICS.map((t) => ({ id: t.id, label: t.label }))} isOn={(t) => topics.includes(t)} onToggle={(t) => ed.setRaw(`items.${id}.topics`, toggle(topics, t))} />
          <div className="grid gap-5 md:grid-cols-2">
            <FieldEditor editor={ed} path={`items.${id}.phone`} def={FIELDS.phone} />
            <FieldEditor editor={ed} path={`items.${id}.email`} def={FIELDS.email} />
          </div>
          <div className="grid gap-3">
            <div className="grid gap-0.5">
              <p className="text-eyebrow uppercase tracking-eyebrow font-medium text-ink">
                Who picks up <span className="ml-2 font-normal normal-case tracking-normal text-dim">Optional</span>
              </p>
              <p className="text-small text-dim">A person’s name and title. Leave blank for a shared line or inbox.</p>
            </div>
            <div className="grid gap-5 md:grid-cols-2">
              <FieldEditor editor={ed} path={`items.${id}.name`} def={FIELDS.name} />
              <FieldEditor editor={ed} path={`items.${id}.role`} def={FIELDS.role} />
            </div>
          </div>
          {locations.order.length > 1 && (
            <Chips
              label="Which locations?"
              options={[{ id: '', label: 'All locations' }, ...locations.order.map((l) => ({ id: l, label: locations.name(l) }))]}
              isOn={(l) => (l === '' ? where.length === 0 : where.includes(l))}
              onToggle={(l) => ed.setRaw(`items.${id}.locationIds`, l === '' ? [] : toggle(where, l))}
            />
          )}
        </div>
      )}
    </section>
  )
}

function Chips({ label, hint, options, isOn, onToggle }: { label: string; hint?: string; options: { id: string; label: string }[]; isOn: (id: string) => boolean; onToggle: (id: string) => void }) {
  return (
    <div role="group" aria-label={label} className="grid gap-2">
      <div className="grid gap-0.5">
        <p className="text-eyebrow uppercase tracking-eyebrow font-medium text-ink">{label}</p>
        {hint && <p className="text-small text-dim">{hint}</p>}
      </div>
      <div className="flex flex-wrap gap-1.5">
        {options.map((o) => (
          <button
            key={o.id || 'all'}
            type="button"
            aria-pressed={isOn(o.id)}
            onClick={() => onToggle(o.id)}
            className={clsx('min-h-8 border px-3 text-small transition-colors', isOn(o.id) ? 'border-ink bg-ink text-paper' : 'border-gray-300 text-charcoal hover:border-ink')}
          >
            {o.label}
          </button>
        ))}
      </div>
    </div>
  )
}

